/**
 * CreditCatalog.jsx — Wave 6 · Batch 3 · Cluster B Turn 2.
 *
 * Full CRUD for the `credit_product_catalog` collection. Personal-credit
 * products surfaced to eligible users via /api/opportunities/personal-credit.
 *
 * Cluster B Turn 2 changes (chrome only):
 *   - Legacy CSS variables and hex literals migrated to the DS token set.
 *   - Filter row wrapped in `_primitives.Section` (matches Cluster A
 *     convention, `credit-catalog-filter-section` testid added).
 *   - Table shell adopts hairline `--ds-color-admin-border` +
 *     `box-shadow: 0 1px 0 0 admin-border` via the `_primitives.Section`
 *     wrapper on the list.
 *   - Search input now uses `_primitives.TextInput` with a positioned
 *     Search icon overlay.
 *   - EditModal migrated to the Wave-6 Batch-3 modal convention:
 *     warm backdrop `rgba(27,26,22,0.55)`, `var(--ds-radius-card)` panel,
 *     `var(--ds-color-admin-border)` hairline, `var(--ds-shadow-modal)`,
 *     sticky footer with `AccentButton` / `GhostButton`.
 *   - PRR (2026-07): EditModal now listens for `Escape` and closes
 *     gracefully — matches the DS `Modal` primitive's ESC contract
 *     (see `design-system/hooks/useFocusTrap.js`). The bespoke shell
 *     intentionally opts out of full focus-trap (per
 *     `modal_convention.md` §4), so this is the surgical piece needed
 *     to close the WCAG-adjacent gap flagged in the Final Executive
 *     Design Audit §8.4.
 *   - Local `TextField` / `TextArea` / `NumField` / `SelectField` wrappers
 *     kept structurally; inner input body replaced by
 *     `_primitives.TextInput` / `_primitives.TextArea` /
 *     `_primitives.SelectInput`. NumField uses TextInput with
 *     `type="number"` — native attr preserved.
 *   - Status pills: hex literals migrated to semantic DS tokens.
 *     Semantic mapping preserved byte-exact (active=success (green),
 *     inactive=danger (red), placeholder/sample=warning (amber)).
 *   - Top action "New product" swapped to `AccentButton`.
 *   - `ConfirmDialog` (destructive) for delete kept as-is; copy byte-exact.
 *
 * BYTE-EXACT PRESERVATIONS (business logic, verified):
 *   - `CATEGORY_OPTIONS` enum (5 entries, unchanged).
 *   - `EMPTY` defaults incl. `min_credit_score: 600`, `is_placeholder:
 *     true`, `is_active: true`.
 *   - `filtered = useMemo` fuzzy-search over `name + provider + category`.
 *   - Delete endpoint `/admin/credit-catalog/${id}` DELETE.
 *   - Toggle-active PATCH payload `{ is_active: !it.is_active }`.
 *   - Required-fields validation list (name, provider, category,
 *     description, primary_benefit, application_link).
 *   - FICO range validation `min < 300 || min > 850` — must not widen
 *     or narrow.
 *   - 13-field POST/PATCH payload shape.
 *   - Create/update dispatch (isNew branch).
 *   - All `credit-catalog-*` testids.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ExternalLink, Loader2, Pencil, Plus, Save, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import ConfirmDialog from "@/design-system/components/ConfirmDialog";
import { api, formatApiError } from "@/api/client";
import {
  Section,
  TextInput,
  TextArea,
  SelectInput,
  GhostButton,
  AccentButton,
} from "@/admin/_primitives";

const CATEGORY_OPTIONS = [
  { value: "credit_builder", label: "Credit builder" },
  { value: "secured", label: "Secured" },
  { value: "balance_transfer", label: "Balance transfer" },
  { value: "zero_apr", label: "0% intro APR" },
  { value: "rewards_starter", label: "Rewards starter" },
];

const EMPTY = {
  name: "",
  provider: "",
  category: "credit_builder",
  description: "",
  min_credit_score: 600,
  typical_apr_low_pct: "",
  typical_apr_high_pct: "",
  annual_fee_usd: 0,
  signup_bonus_note: "",
  primary_benefit: "",
  eligibility_notes: "",
  application_link: "",
  is_placeholder: true,
  is_active: true,
};

export default function AdminCreditCatalog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | product
  const [confirmingDelete, setConfirmingDelete] = useState(null); // null | product
  const [deleteBusy, setDeleteBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/credit-catalog");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((it) => `${it.name} ${it.provider} ${it.category}`.toLowerCase().includes(needle));
  }, [items, q]);

  async function onDelete(id, name) {
    try {
      await api.delete(`/admin/credit-catalog/${id}`);
      toast.success("Product deleted.");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function onToggleActive(it) {
    try {
      await api.patch(`/admin/credit-catalog/${it.id}`, { is_active: !it.is_active });
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <AdminLayout
      title="Credit catalog"
      subtitle="Personal-credit products surfaced to eligible users via /api/opportunities/personal-credit"
      actions={
        <AccentButton
          data-testid="credit-catalog-new"
          onClick={() => setEditing("new")}
        >
          <Plus className="w-3.5 h-3.5" /> New product
        </AccentButton>
      }
    >
      <AdminPageHelp
        title="Credit Catalog"
        purpose="Manages the catalog of personal-credit products (secured cards, credit-builder loans, subprime approvals) surfaced to eligible users via /api/opportunities/personal-credit."
        configures="Product name, provider, category, credit-score range and 'is_active' flag. Products are matched to users by their current score and profile, so accurate ranges are critical."
        impact="Every eligible-user opportunities feed and every recommendation modal picks up the change on next page load. Deactivating a product hides it immediately; the product row is not removed so historical clicks are preserved."
        safety="Yes. Any product can be re-enabled by toggling 'is_active' back on. Score-range edits are non-destructive — only the matching logic uses them."
      />
      <div className="space-y-3 max-w-6xl">
        <Section title="Filter" testid="credit-catalog-filter-section">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-md">
              <Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5"
                style={{ color: "var(--ds-color-ink-muted)" }}
                aria-hidden="true"
              />
              <TextInput
                type="text"
                data-testid="credit-catalog-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, provider or category…"
                className="w-full pl-8"
              />
            </div>
            <div
              className="text-xs tabular-nums"
              data-testid="credit-catalog-count"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              {filtered.length} / {items.length} products
            </div>
          </div>
        </Section>

        {loading ? (
          <div
            className="text-sm px-3 py-2"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="rounded-[var(--ds-radius-card)] p-6 text-sm"
            style={{
              background: "var(--ds-color-surface)",
              border: "1px solid var(--ds-color-admin-border)",
              color: "var(--ds-color-ink-muted)",
              boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
            }}
          >
            No products match your search.
          </div>
        ) : (
          <Section title={`Products (${filtered.length})`} testid="credit-catalog-section" bodyClassName="p-0">
            <div className="overflow-x-auto" data-testid="credit-catalog-table">
              <table className="w-full text-[13px]">
                <thead
                  className="text-[10px] uppercase tracking-[0.14em]"
                  style={{
                    background: "var(--ds-color-admin-surface-muted)",
                    color: "var(--ds-color-ink-muted)",
                  }}
                >
                  <tr>
                    <th className="text-left px-4 py-2">Name</th>
                    <th className="text-left px-4 py-2">Category</th>
                    <th className="text-left px-4 py-2 tabular-nums">Min score</th>
                    <th className="text-left px-4 py-2 tabular-nums">Annual fee</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-right px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => (
                    <tr
                      key={it.id}
                      data-testid={`credit-catalog-row-${it.id}`}
                      className="transition-colors"
                      style={{
                        borderTop: "1px solid var(--ds-color-admin-border)",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--ds-color-admin-surface-muted)")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <td className="px-4 py-2">
                        <div className="font-medium" style={{ color: "var(--ds-color-ink)" }}>{it.name}</div>
                        <div className="text-xs" style={{ color: "var(--ds-color-ink-muted)" }}>{it.provider}</div>
                      </td>
                      <td
                        className="px-4 py-2 uppercase text-[10px] tracking-[0.12em]"
                        style={{ color: "var(--ds-color-ink-muted)" }}
                      >
                        {it.category?.replace(/_/g, " ")}
                      </td>
                      <td className="px-4 py-2 tabular-nums" style={{ color: "var(--ds-color-ink)" }}>{it.min_credit_score}</td>
                      <td className="px-4 py-2 tabular-nums" style={{ color: "var(--ds-color-ink-muted)" }}>
                        ${Number(it.annual_fee_usd || 0).toFixed(0)}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <StatusPill
                            active={it.is_active}
                            testid={`credit-catalog-toggle-active-${it.id}`}
                            onClick={() => onToggleActive(it)}
                          />
                          {it.is_placeholder && <SamplePill />}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="inline-flex items-center gap-0.5">
                          {it.application_link && (
                            <a
                              href={it.application_link}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`credit-catalog-link-${it.id}`}
                              className="p-1.5 rounded-[var(--ds-radius-input)] transition-colors"
                              style={{ color: "var(--ds-color-ink-muted)" }}
                              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
                              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
                              title="Open application link"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                          <button
                            type="button"
                            data-testid={`credit-catalog-edit-${it.id}`}
                            onClick={() => setEditing(it)}
                            className="p-1.5 rounded-[var(--ds-radius-input)] transition-colors"
                            style={{ color: "var(--ds-color-ink-muted)" }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
                            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
                            title="Edit"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            data-testid={`credit-catalog-delete-${it.id}`}
                            onClick={() => setConfirmingDelete(it)}
                            className="p-1.5 rounded-[var(--ds-radius-input)] transition-colors"
                            style={{ color: "var(--ds-color-danger)" }}
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}
      </div>

      {editing && (
        <EditModal
          product={editing === "new" ? EMPTY : editing}
          isNew={editing === "new"}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}

      <ConfirmDialog
        open={!!confirmingDelete}
        onCancel={() => setConfirmingDelete(null)}
        onConfirm={() => confirmingDelete && onDelete(confirmingDelete.id)}
        busy={deleteBusy}
        variant="destructive"
        title={confirmingDelete ? `Delete "${confirmingDelete.name}"?` : "Delete product"}
        description="Deleting this product removes it from the credit catalog. It will no longer appear in any user's opportunities view."
        confirmLabel="Delete product"
        testId="credit-catalog-delete-confirm"
      />
    </AdminLayout>
  );
}

/* ------------------------------------------------------------------ */
/* Status pills — semantic color mapping preserved byte-exact          */
/*   active=true   → success (green)                                   */
/*   active=false  → danger  (red)                                     */
/*   placeholder   → warning (amber)                                   */
/* ------------------------------------------------------------------ */

function StatusPill({ active, testid, onClick }) {
  const tone = active ? "success" : "danger";
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] transition-colors"
      style={{
        background: `var(--ds-color-${tone}-soft)`,
        color: `var(--ds-color-${tone})`,
        border: `1px solid var(--ds-color-${tone})`,
      }}
    >
      {active ? "Active" : "Inactive"}
    </button>
  );
}

function SamplePill() {
  return (
    <span
      className="rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.14em]"
      style={{
        background: "var(--ds-color-warning-soft)",
        color: "var(--ds-color-warning)",
        border: "1px solid var(--ds-color-warning)",
      }}
    >
      Sample
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* EditModal — bespoke editor shell per modal_convention.md §2         */
/* ------------------------------------------------------------------ */

function EditModal({ product, isNew, onClose, onSaved }) {
  const [form, setForm] = useState(product);
  const [saving, setSaving] = useState(false);

  // A11y — Escape closes the modal, matching the DS `Modal` primitive's
  // ESC pattern (see `design-system/hooks/useFocusTrap.js`). The bespoke
  // shell intentionally opts out of the full focus-trap (per
  // `modal_convention.md` §4), but Escape-to-dismiss is table-stakes.
  useEffect(() => {
    const handleEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  function upd(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function save() {
    // Validate required fields.
    if (!form.name?.trim() || !form.provider?.trim() || !form.category || !form.description?.trim() || !form.primary_benefit?.trim() || !form.application_link?.trim()) {
      toast.error("Name, provider, category, description, primary benefit and application link are all required.");
      return;
    }
    const min = Number(form.min_credit_score);
    if (!Number.isFinite(min) || min < 300 || min > 850) {
      toast.error("Min credit score must be between 300 and 850.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        provider: form.provider.trim(),
        category: form.category,
        description: form.description.trim(),
        min_credit_score: min,
        typical_apr_low_pct: form.typical_apr_low_pct === "" ? null : Number(form.typical_apr_low_pct),
        typical_apr_high_pct: form.typical_apr_high_pct === "" ? null : Number(form.typical_apr_high_pct),
        annual_fee_usd: Number(form.annual_fee_usd || 0),
        signup_bonus_note: form.signup_bonus_note?.trim() || null,
        primary_benefit: form.primary_benefit.trim(),
        eligibility_notes: form.eligibility_notes?.trim() || null,
        application_link: form.application_link.trim(),
        is_placeholder: !!form.is_placeholder,
        is_active: !!form.is_active,
      };
      if (isNew) {
        await api.post("/admin/credit-catalog", payload);
        toast.success("Product created.");
      } else {
        await api.patch(`/admin/credit-catalog/${product.id}`, payload);
        toast.success("Product updated.");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      data-testid="credit-catalog-modal"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8 px-4"
      style={{ background: "rgba(27,26,22,0.55)" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-[var(--ds-radius-card)] overflow-hidden"
        style={{
          background: "var(--ds-color-surface)",
          border: "1px solid var(--ds-color-admin-border)",
          boxShadow: "var(--ds-shadow-modal)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex items-center justify-between px-5 py-3"
          style={{ borderBottom: "1px solid var(--ds-color-admin-border)" }}
        >
          <h3 className="font-display text-sm" style={{ color: "var(--ds-color-ink)" }}>
            {isNew ? "New product" : `Edit — ${product.name}`}
          </h3>
          <button
            type="button"
            data-testid="credit-catalog-modal-close"
            onClick={onClose}
            className="p-1 transition-colors"
            style={{ color: "var(--ds-color-ink-muted)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--ds-color-ink)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ds-color-ink-muted)")}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 py-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="grid md:grid-cols-2 gap-4">
            <TextField label="Name" testId="credit-catalog-field-name" value={form.name} onChange={(v) => upd("name", v)} />
            <TextField label="Provider" testId="credit-catalog-field-provider" value={form.provider} onChange={(v) => upd("provider", v)} />
            <SelectField label="Category" testId="credit-catalog-field-category" value={form.category} onChange={(v) => upd("category", v)} options={CATEGORY_OPTIONS} />
            <NumField label="Min credit score (300-850)" testId="credit-catalog-field-min-score" value={form.min_credit_score} onChange={(v) => upd("min_credit_score", v)} />
            <NumField label="Typical APR low (%)" testId="credit-catalog-field-apr-low" value={form.typical_apr_low_pct} onChange={(v) => upd("typical_apr_low_pct", v)} step={0.1} />
            <NumField label="Typical APR high (%)" testId="credit-catalog-field-apr-high" value={form.typical_apr_high_pct} onChange={(v) => upd("typical_apr_high_pct", v)} step={0.1} />
            <NumField label="Annual fee ($)" testId="credit-catalog-field-fee" value={form.annual_fee_usd} onChange={(v) => upd("annual_fee_usd", v)} />
            <TextField label="Signup bonus note (optional)" testId="credit-catalog-field-bonus" value={form.signup_bonus_note} onChange={(v) => upd("signup_bonus_note", v)} />
          </div>
          <TextField label="Primary benefit (one line)" testId="credit-catalog-field-benefit" value={form.primary_benefit} onChange={(v) => upd("primary_benefit", v)} />
          <TextAreaField label="Description" testId="credit-catalog-field-description" value={form.description} onChange={(v) => upd("description", v)} rows={3} />
          <TextAreaField label="Eligibility notes (optional)" testId="credit-catalog-field-eligibility" value={form.eligibility_notes} onChange={(v) => upd("eligibility_notes", v)} rows={2} />
          <TextField label="Application link" testId="credit-catalog-field-link" value={form.application_link} onChange={(v) => upd("application_link", v)} placeholder="https://…" />
          <div className="flex items-center gap-5 flex-wrap">
            <label
              className="flex items-center gap-2 text-sm cursor-pointer"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              <input
                type="checkbox"
                data-testid="credit-catalog-field-placeholder"
                checked={!!form.is_placeholder}
                onChange={(e) => upd("is_placeholder", e.target.checked)}
                className="w-4 h-4"
                style={{ accentColor: "var(--ds-color-accent)" }}
              />
              Sample entry (swap in real offer before launch)
            </label>
            <label
              className="flex items-center gap-2 text-sm cursor-pointer"
              style={{ color: "var(--ds-color-ink-muted)" }}
            >
              <input
                type="checkbox"
                data-testid="credit-catalog-field-active"
                checked={!!form.is_active}
                onChange={(e) => upd("is_active", e.target.checked)}
                className="w-4 h-4"
                style={{ accentColor: "var(--ds-color-accent)" }}
              />
              Active
            </label>
          </div>
        </div>
        <div
          className="flex items-center justify-end gap-2 px-5 py-3"
          style={{
            borderTop: "1px solid var(--ds-color-admin-border)",
            background: "var(--ds-color-admin-surface-muted)",
          }}
        >
          <GhostButton
            data-testid="credit-catalog-modal-cancel"
            onClick={onClose}
          >
            Cancel
          </GhostButton>
          <AccentButton
            data-testid="credit-catalog-modal-save"
            onClick={save}
            disabled={saving}
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {saving ? "Saving…" : isNew ? "Create" : "Save changes"}
          </AccentButton>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Local field wrappers — label+span shape kept structurally,          */
/* inner input body swapped to `_primitives` per Turn 1 portability    */
/* assessment (Field NOT lifted; per-file wrapping shape stays).       */
/* ------------------------------------------------------------------ */

function TextField({ label, value, onChange, testId, placeholder }) {
  return (
    <label className="block text-xs">
      <FieldLabel>{label}</FieldLabel>
      <TextInput
        type="text"
        data-testid={testId}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full"
      />
    </label>
  );
}

function TextAreaField({ label, value, onChange, rows = 3, testId }) {
  return (
    <label className="block text-xs">
      <FieldLabel>{label}</FieldLabel>
      <TextArea
        data-testid={testId}
        value={value ?? ""}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
        className="w-full"
      />
    </label>
  );
}

function NumField({ label, value, onChange, step = 1, testId }) {
  return (
    <label className="block text-xs">
      <FieldLabel>{label}</FieldLabel>
      <TextInput
        type="number"
        data-testid={testId}
        value={value ?? ""}
        step={step}
        onChange={(e) => onChange(e.target.value)}
        className="w-full tabular-nums"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <label className="block text-xs">
      <FieldLabel>{label}</FieldLabel>
      <SelectInput
        data-testid={testId}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </SelectInput>
    </label>
  );
}

function FieldLabel({ children }) {
  return (
    <span
      className="block mb-1 uppercase tracking-[0.12em] text-[10px]"
      style={{ color: "var(--ds-color-ink-muted)" }}
    >
      {children}
    </span>
  );
}
